import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { BookOpen, ArrowLeft, Send, CheckCircle, Loader2, User, Mail, Phone, DollarSign } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const coursePlans = [
  {
    name: "Digital Growth Mastery",
    tier: "Standard",
    price: "$500",
    duration: "8 Weeks",
    goal: "Become a Freelancer",
  },
  {
    name: "Digital Growth Elite",
    tier: "Premium",
    price: "$1,000",
    duration: "12 Weeks",
    goal: "Build Your Business",
  },
];

const CourseApplicationPage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    experience_level: "",
    selected_plan: "",
    goals: "",
    how_heard: "",
    agreed_to_terms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Auto-select the plan from ?plan=... query param sent by LearnPage CTAs
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const wanted = params.get("plan");
    if (!wanted) return;
    const match = coursePlans.find((p) => p.name === wanted);
    if (match) {
      setFormData((prev) => ({ ...prev, selected_plan: match.name }));
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/course/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitStatus({ type: "success", id: data.application_id });
      } else {
        setSubmitStatus({ type: "error", message: data.detail || "Submission failed" });
      }
    } catch (error) {
      setSubmitStatus({ type: "error", message: "Network error. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-blue-600" />
          </div>
          <h2 className="font-['Poppins'] text-2xl font-bold text-slate-800 mb-4">Application Submitted!</h2>
          <p className="text-slate-600 mb-6">
            Thank you for your interest in our Pay Per Call training program.<br /><br />
            Our team will review your application and contact you within 24-48 hours.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Application ID: <span className="font-mono">{submitStatus.id}</span>
          </p>
          <Link 
            to="/learn"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-6 py-3 rounded-full hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Courses
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 font-['Inter']">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="DMM" className="h-12 sm:h-14 w-auto object-contain" />
          </Link>
          <Link to="/learn" className="text-slate-500 hover:text-blue-600 text-sm flex items-center gap-1 font-medium">
            <ArrowLeft className="w-4 h-4" />
            Back to Courses
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] text-white py-12 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm mb-4 border border-white/10">
            <BookOpen className="w-4 h-4" />
            Course Application
          </div>
          <h1 className="font-['Poppins'] text-3xl sm:text-4xl font-bold mb-3">
            Apply for <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Training Program</span>
          </h1>
          <p className="text-white/80 text-sm sm:text-base max-w-xl mx-auto">
            Complete the form below to apply for our Pay Per Call training program.
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-4 sm:p-6 -mt-6" data-testid="course-form">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
          <h3 className="font-semibold text-lg text-slate-800 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Personal Information
          </h3>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
              <input
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="+1 555 123 4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
              <input
                type="text"
                required
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="United States"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
          <h3 className="font-semibold text-lg text-slate-800 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            Course Selection
          </h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Your Plan *</label>
            <div className="space-y-2">
              {coursePlans.map((plan, index) => (
                <label
                  key={index}
                  className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${
                    formData.selected_plan === plan.name
                      ? "bg-gradient-to-r from-blue-50 to-purple-50 border-blue-500"
                      : "border-slate-200 hover:border-blue-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="selected_plan"
                      value={plan.name}
                      checked={formData.selected_plan === plan.name}
                      onChange={(e) => setFormData({ ...formData, selected_plan: e.target.value })}
                      className="w-4 h-4 text-blue-600"
                      required
                    />
                    <div>
                      <span className="font-semibold text-slate-800">{plan.name}</span>
                      <span className="text-slate-500 text-sm ml-2">({plan.duration})</span>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {plan.tier} · {plan.goal}
                      </p>
                    </div>
                  </div>
                  <span className="font-bold text-blue-600">{plan.price}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Experience Level *</label>
            <select
              required
              value={formData.experience_level}
              onChange={(e) => setFormData({ ...formData, experience_level: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
            >
              <option value="">Select your experience</option>
              <option value="Complete Beginner">Complete Beginner</option>
              <option value="Some Digital Marketing Experience">Some Digital Marketing Experience</option>
              <option value="Experienced Affiliate Marketer">Experienced Affiliate Marketer</option>
              <option value="Pay Per Call Professional">Pay Per Call Professional</option>
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">What are your goals? *</label>
            <textarea
              required
              value={formData.goals}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
              placeholder="Tell us what you want to achieve..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">How did you hear about us?</label>
            <select
              value={formData.how_heard}
              onChange={(e) => setFormData({ ...formData, how_heard: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
            >
              <option value="">Select an option</option>
              <option value="Google Search">Google Search</option>
              <option value="Social Media">Social Media</option>
              <option value="Referral">Referral</option>
              <option value="YouTube">YouTube</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        {/* Terms */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              required
              checked={formData.agreed_to_terms}
              onChange={(e) => setFormData({ ...formData, agreed_to_terms: e.target.checked })}
              className="mt-1 w-5 h-5 text-blue-600 rounded"
            />
            <span className="text-sm text-slate-600">
              I agree to the <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>. I understand that course fees are non-refundable.
            </span>
          </label>
        </div>

        {submitStatus?.type === "error" && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-6">
            {submitStatus.message}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !formData.agreed_to_terms}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold text-lg py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Submit Application
            </>
          )}
        </button>
      </form>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <img src="/logo.png" alt="DMM" className="h-12 w-auto mx-auto mb-4 object-contain" />
          <p className="text-slate-400 text-sm">Premium Pay-Per-Call Network</p>
          <p className="text-slate-500 text-xs mt-4">© {new Date().getFullYear()} Digital Master Media LLC</p>
        </div>
      </footer>
    </div>
  );
};

export default CourseApplicationPage;
